import React, { useState, useEffect } from "react";
import { toast, ToastContainer } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import { getData, postData } from "../../../utility/Utility";
import CustomLoader from "../../../Components/CustomLoader/CustomLoader";

function RewardSettings() {
  const [reward, setReward] = useState({
    rewardPointValue: "",
    rewardPointExpiryDate: "",
    emailSendfrom: "",
    voucherRedemptionTime: {
      startTime: "",
      endTime: "",
    },
  });
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);

  // Fetch reward data
  const fetchReward = async () => {
    setLoading(true);
    try {
      const response = await getData("api/settings/reward");
      if (response.success && response.data) {
        setReward({
          rewardPointValue: response.data.rewardPointValue,
          rewardPointExpiryDate: response.data.rewardPointExpiryDate,
          emailSendfrom: response.data.emailSendfrom,
          voucherRedemptionTime: response.data.voucherRedemptionTime || {
            startTime: "",
            endTime: "",
          },
        });
      } else {
        toast.error(response.message || "Failed to fetch reward setting");
      }
    } catch (error) {
      console.error("Error fetching reward:", error);
      toast.error("Something went wrong while fetching reward setting");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchReward();
  }, []);

  // Handle simple field changes
  const handleChange = (e) => {
    const { name, value } = e.target;
    setReward((prev) => ({ ...prev, [name]: value }));
  };

  // Handle voucher redemption time changes
  const handleRedemptionTimeChange = (e) => {
    const { name, value } = e.target;
    setReward((prev) => ({
      ...prev,
      voucherRedemptionTime: {
        ...prev.voucherRedemptionTime,
        [name]: value,
      },
    }));
  };

  // Submit form with validation
  const handleSubmit = async (e) => {
    e.preventDefault();

    const { startTime, endTime } = reward.voucherRedemptionTime;

    // Validation: End time must be after start time
    if (startTime && endTime && startTime >= endTime) {
      toast.error("End time must be after start time!");
      return;
    }

    setSaving(true);
    try {
      const response = await postData("api/settings/reward", reward);
      if (response.success) {
        toast.success("Reward updated successfully!");
        setReward({
          rewardPointValue: response.data.rewardPointValue,
          rewardPointExpiryDate: response.data.rewardPointExpiryDate,
          emailSendfrom: response.data.emailSendfrom,
          voucherRedemptionTime: response.data.voucherRedemptionTime,
        });
      } else {
        toast.error(response.message || "Failed to update reward");
      }
    } catch (error) {
      console.error("Error updating reward:", error);
      toast.error("Something went wrong while updating reward");
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="content-page">
      <ToastContainer position="top-right" autoClose={3000} />
      <CustomLoader loading={loading || saving} />
      <div className="content">
        <div className="container-fluid">
          <div className="py-3">
            <h4 className="fs-18 fw-semibold m-0">Project Reward Setting</h4>
            <p className="fw-semibold my-2">Update reward details below</p>
          </div>

          {/* Form card */}
          <div className="card mt-3">
            <div className="card-body">
              <form onSubmit={handleSubmit}>
                <div className="row mb-3">
                  {/* Reward Point Value */}
                  <div className="col-md-4">
                    <label className="form-label">Reward Point Value</label>
                    <input
                      type="number"
                      min="0"
                      name="rewardPointValue"
                      className="form-control"
                      value={reward.rewardPointValue}
                      onChange={handleChange}
                      required
                    />
                  </div>

                  {/* Reward Point Expiry Date */}
                  <div className="col-md-4">
                    <label className="form-label">
                      Reward Point Expiry Date
                    </label>
                    <input
                      type="date"
                      name="rewardPointExpiryDate"
                      className="form-control"
                      value={reward.rewardPointExpiryDate}
                      onChange={handleChange}
                      min={new Date().toISOString().split("T")[0]}
                      required
                    />
                  </div>
                </div>

                <h5 className="mt-4 mb-3">Voucher Redemption Time</h5>
                <div className="row mb-3">
                  <div className="col-md-4">
                    <label className="form-label">Start Time</label>
                    <input
                      type="time"
                      name="startTime"
                      className="form-control"
                      value={reward.voucherRedemptionTime?.startTime}
                      onChange={handleRedemptionTimeChange}
                      required
                    />
                  </div>
                  <div className="col-md-4">
                    <label className="form-label">End Time</label>
                    <input
                      type="time"
                      name="endTime"
                      className="form-control"
                      value={reward.voucherRedemptionTime?.endTime}
                      onChange={handleRedemptionTimeChange}
                      required
                    />
                  </div>
                </div>

                <h5 className="mt-4 mb-3">Email Configuration</h5>
                <div className="row mb-3">
                  <div className="col-md-6">
                    <label className="form-label">
                      System Email Address
                      <small className="text-muted d-block">
                        All system notifications and emails will be sent from
                        this address
                      </small>
                    </label>
                    <input
                      type="email"
                      name="emailSendfrom"
                      className="form-control"
                      placeholder="admin@yourcompany.com"
                      value={reward.emailSendfrom}
                      onChange={handleChange}
                      required
                    />
                  </div>
                </div>

                {/* Submit button aligned right */}
                <div className="d-flex justify-content-end">
                  <button
                    type="submit"
                    className="btn btn-primary"
                    disabled={saving}
                  >
                    {saving ? "Saving..." : "Update Reward"}
                  </button>
                </div>
              </form>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

export default RewardSettings;
